import React, { useCallback, useState } from 'react';
import { NextPageContext } from 'next';

import {
  Activities,
  Address,
  Amenities,
  Breadcrumbs,
  ContactButtons,
  CtaButtons,
  EmailButton,
  FAQs,
  Floorplans,
  HeroUnit,
  PhoneButton,
  PhoneLink,
  PhotoModal,
  ServicesGlobals,
  TourTimes,
  UnitProperties,
  Units,
  VideoModal,
} from '../../../../components';
import Image from '../../../../components/layout/Image';
import { useSiteQuery } from '../../../../hooks';
import { getRetirementVillageTitleByUnit } from '../../../../lib/FunctionLib';
import ErrorPage from '../../../_error';
import NotFoundPage from '../../../404';

import query from './index.graphql';

import styles from './index.module.scss';

interface Props {
  villageSlug: string;
  slug: string;
}

const RetirementVillageUnit = ({ villageSlug, slug }: Props) => {
  const [{ data, error }] = useSiteQuery<RetirementVillageUnitQuery>({ query, variables: { slug } });
  const [photoModalOpen, openPhotoModal] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const togglePhotoModal = useCallback(() => {
    openPhotoModal(!photoModalOpen);
  }, [photoModalOpen]);

  const toggleVideoModal = useCallback(() => {
    setVideoOpen(!videoOpen);
  }, [videoOpen]);

  function onGalleryClick(e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) {
    if (e.type !== 'keypress' || (e as React.KeyboardEvent).key === 'Enter') {
      togglePhotoModal();
    }
  }

  function onVideoClick(e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) {
    if (e.type !== 'keypress' || (e as React.KeyboardEvent).key === 'Enter') {
      toggleVideoModal();
    }
  }

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  const entry = data?.entries?.filter(
    (e): e is UnitFragment => e !== null && e !== undefined && e.retirementVillageLocation?.[0]?.slug === villageSlug,
  )[0];
  const villageEntry = entry?.retirementVillageLocation?.[0];

  if (entry && villageEntry) {
    const heading = getRetirementVillageTitleByUnit(entry);
    const title = entry.title!;
    const hideCtas = villageEntry.hideContactCtas;
    const photos = entry.photos?.filter((p): p is AssetFragment => p !== null && p !== undefined) || [];
    const floorplans = entry.floorplans || [];
    const image = entry.image?.[0];

    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Retirement Villages', uri: '/retirement-villages' },
      { title: 'Retirement Village Locations', uri: '/retirement-villages/locations' },
      { title: villageEntry.title!, uri: `/${villageEntry.uri!}` },
      { title, uri: `/${entry.uri!}` },
    ];

    return (
      <>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <HeroUnit
          sectionHeading={heading}
          title={title}
          photos={photos}
          videoUrl={entry.videoUrl!}
          onGalleryClick={onGalleryClick}
          onVideoClick={onVideoClick}
        >
          <Address address={entry.address} className={styles.unitAddress} inline />
          <UnitProperties unit={entry} />
          <p className={styles.term}>* Terms & conditions apply</p>
          <hr />
          {!hideCtas && (
            <div>
              <p className={styles.enquire}>Enquire about this unit</p>
              <ContactButtons customPhoneNumber={entry.phone!} mobile>
                {villageEntry.tourTimes && <TourTimes time={villageEntry.tourTimes} className={styles.tourTimes} />}
              </ContactButtons>
              <div className="desktopOnly">
                <EmailButton section="Retirement Village Location Unit" name={`${villageEntry.title!} ${entry.title!}`} />
                <div>
                  <PhoneLink number={entry.phone!} text="Call us on" />
                </div>
                {villageEntry.tourTimes && <TourTimes time={villageEntry.tourTimes} className={styles.tourTimes} />}
              </div>
            </div>
          )}
        </HeroUnit>
        <div className="section left">
          <div className="container">
            <div className="row">
              {floorplans.length === 0 && image && (
                <div className="col-lg-6">
                  <div className={styles.overviewImage}>
                    <Image {...image} alt={title} />
                  </div>
                </div>
              )}
              <div className="col-lg-6">
                <div id="description" className="sectionHeading">
                  Village Overview
                </div>
                <h2>About {entry.title}</h2>
                <div dangerouslySetInnerHTML={{ __html: entry.body! }} />
              </div>
              {floorplans.length > 0 && (
                <div className="col-lg-6">
                  <Floorplans floorplans={floorplans} />
                </div>
              )}
              {!hideCtas && floorplans.length === 0 && !image && (
                <div className="col-lg-6">
                  <CtaButtons
                    headline="Get in touch for more details about this property"
                    description="Send an email or give us a call during business hours"
                  >
                    <div>
                      <EmailButton
                        type="orangeBorder"
                        section="Retirement Village Location Unit"
                        name={`${villageEntry.title!} ${entry.title!}`}
                      />
                    </div>
                    <div>
                      <PhoneButton customPhone={entry.phone!} hideText />
                    </div>
                  </CtaButtons>
                </div>
              )}
            </div>
          </div>
        </div>
        {villageEntry.activities! && <Activities activities={villageEntry.activities} />}
        <ServicesGlobals />
        <Amenities title={entry.title!} address={entry.address!} amenities={villageEntry?.amenities} />
        <Units location={villageEntry.id!} except={entry.slug!} />
        <FAQs topic="retirement-villages" />
        {photoModalOpen && photos && <PhotoModal photos={photos} handleClose={togglePhotoModal} />}
        {videoOpen && entry.videoUrl! && (
          <VideoModal isOpen={videoOpen} video={entry.videoUrl!} onRequestClose={toggleVideoModal} />
        )}
      </>
    );
  }
  return <NotFoundPage />;
};

RetirementVillageUnit.getInitialProps = ({ req }: NextPageContext) => ({
  villageSlug: req?.url?.split('/')?.[2] || '',
  slug: req?.url?.substring(req?.url?.lastIndexOf('/') + 1).replace(/\?.*$/, ''),
});

export default RetirementVillageUnit;
